import type {
  Anmerkung,
  EntscheidungsPayload,
  EntwurfSegmentEintrag,
  NutzungsbedingungenStand,
  PdpAktion,
  PdpDetails,
  PdpEntscheidungsAngaben,
  StartAnfrage,
  TextAntwort,
} from '@awf/shared';
import { initFirebase } from '../firebase.js';
import { ALLGEMEINER_FEHLERTEXT, DIENST_NICHT_ERREICHBAR_TEXT } from './errors.js';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly text: string,
    readonly status: number,
  ) {
    super(`${code}: ${text}`);
    this.name = 'ApiError';
  }
}

async function bearerToken(): Promise<string> {
  const { auth } = await initFirebase();
  const nutzer = auth.currentUser;
  if (!nutzer) {
    throw new ApiError('nicht_angemeldet', 'Bitte melden Sie sich an.', 401);
  }
  return nutzer.getIdToken();
}

function istJson(antwort: Response): boolean {
  return (antwort.headers.get('content-type') ?? '').includes('application/json');
}

/**
 * `methode` explizit statt aus dem Vorhandensein eines Koerpers abgeleitet: Ein GET traegt
 * weder Koerper noch `Content-Type`, und das soll an der Aufrufstelle stehen und nicht aus
 * einem `undefined` weiter unten folgen. `PUT` wird wie `POST` behandelt (Koerper + JSON).
 * `DELETE` (Task-13-Review HOCH-2, `loescheEntwurf`) faellt wie `GET` in den koerperlosen
 * Zweig -- die Route braucht keinen Koerper, nur die Pfad-ID.
 */
async function ruf<T>(
  pfad: string,
  methode: 'GET' | 'POST' | 'PUT' | 'DELETE',
  koerper?: unknown,
): Promise<T> {
  const token = await bearerToken();
  const ohneKoerper = methode === 'GET' || methode === 'DELETE';

  let antwort: Response;
  try {
    // `GET`/`DELETE` bekommen dieselbe Sonderbehandlung (kein Koerper, kein
    // `Content-Type`); `POST`/`PUT` fallen in denselben Zweig.
    antwort = await fetch(pfad, {
      method: methode,
      headers: ohneKoerper
        ? { Authorization: `Bearer ${token}` }
        : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(ohneKoerper ? {} : { body: JSON.stringify(koerper) }),
    });
  } catch {
    throw new ApiError('netzwerk', DIENST_NICHT_ERREICHBAR_TEXT, 0);
  }

  // Eine Antwort ohne JSON ist NIE ein Erfolg, auch nicht mit Status 200: Greift der
  // Hosting-Rewrite nicht, liefert der SPA-Fallback index.html mit 200. Ohne diese Pruefung
  // ginge das als leeres Ergebnis durch und die Oberflaeche saehe leer aus statt kaputt.
  if (!istJson(antwort)) {
    throw new ApiError('antwort_unlesbar', DIENST_NICHT_ERREICHBAR_TEXT, antwort.status);
  }

  const daten = (await antwort.json()) as unknown;

  if (!antwort.ok) {
    const fehler = (daten as { fehler?: { code?: unknown; text?: unknown } }).fehler;
    const code = typeof fehler?.code === 'string' ? fehler.code : 'unbekannt';
    const text = typeof fehler?.text === 'string' ? fehler.text : ALLGEMEINER_FEHLERTEXT;
    throw new ApiError(code, text, antwort.status);
  }

  return daten as T;
}

/** Ob die aktuelle Fassung der Nutzungsbedingungen bestaetigt ist (Willkommensdialog). */
export function holeNutzungsbedingungen(): Promise<NutzungsbedingungenStand> {
  return ruf<NutzungsbedingungenStand>('/api/nutzungsbedingungen', 'GET');
}

/** Bestaetigt die angezeigte Fassung; den Zeitpunkt setzt das Backend. */
export function bestaetigeNutzungsbedingungen(version: string): Promise<NutzungsbedingungenStand> {
  return ruf<NutzungsbedingungenStand>('/api/nutzungsbedingungen', 'POST', { version });
}

export function starteLauf(anfrage: StartAnfrage): Promise<{ runId: string }> {
  return ruf<{ runId: string }>('/api/runs', 'POST', anfrage);
}

/** Loescht einen eigenen Auftrag endgueltig (`DELETE /runs/:id`, Uebersicht). */
export async function loescheAuftrag(runId: string): Promise<void> {
  await ruf<{ geloescht: string }>(`/api/runs/${encodeURIComponent(runId)}`, 'DELETE');
}

export async function sendeEntscheidung(
  runId: string,
  payload: EntscheidungsPayload,
): Promise<void> {
  await ruf<{ angenommen: true }>(
    `/api/runs/${encodeURIComponent(runId)}/decision`,
    'POST',
    payload,
  );
}

/** Das Entscheidungstor eines PDP-Auftrags (`POST /runs/:id/pdp-entscheidung`). */
export async function sendePdpEntscheidung(
  runId: string,
  aktion: PdpAktion,
  angaben?: PdpEntscheidungsAngaben,
): Promise<void> {
  await ruf<{ angenommen: true }>(
    `/api/runs/${encodeURIComponent(runId)}/pdp-entscheidung`,
    'POST',
    angaben ? { aktion, ...angaben } : { aktion },
  );
}

/**
 * Holt die gerenderte Vorschau ueber die Zwischenschicht. Der Browser spricht dabei NICHT
 * mit n8n (Architekturregel 1) -- und er koennte es an dieser Stelle auch gar nicht
 * authentifiziert tun: Ein `<a target="_blank">` traegt keinen `Authorization`-Kopf, eine
 * Tab-Navigation ist kein `fetch`. Genau deshalb wird die Vorschau eingebettet angezeigt
 * statt in einem eigenen Tab (Entscheidung vom 15.09.2026, siehe Learnings-Log).
 */
export async function holeVorschau(runId: string): Promise<string> {
  const { html } = await ruf<{ html: unknown }>(
    `/api/runs/${encodeURIComponent(runId)}/vorschau`,
    'GET',
  );
  // Die Form wird HIER geprueft, nicht beim Aufrufer: Dort stuende `html.trim()` im
  // Erfolgszweig, und eine Ausnahme von dort erreicht den danebenstehenden Fehlerzweig
  // nicht -- die Seite bliebe im Ladezustand haengen. Der leere String ist dagegen gueltig:
  // Er ist die Auskunft "es ist nichts da" (siehe `bereiteVorschauAuf` im Backend).
  if (typeof html !== 'string') {
    throw new ApiError('antwort_unlesbar', ALLGEMEINER_FEHLERTEXT, 200);
  }
  return html;
}

/** Holt den bearbeitbaren Ratgeber-Text ueber die Zwischenschicht (Architekturregel 1). */
export function holeText(runId: string): Promise<TextAntwort> {
  return ruf<TextAntwort>(`/api/runs/${encodeURIComponent(runId)}/text`, 'GET');
}

/**
 * Die volle Form von `PdpDetails`, nicht nur die ersten zwei Felder: Fehlten `usps`, `faq` oder
 * `wettbewerber`, stuerzte die Darstellung ab, statt den Fehlerzustand zu zeigen (Story
 * "PDP-Entscheidungsseite: Kleinkram").
 */
function istPdpDetails(d: unknown): d is PdpDetails {
  if (typeof d !== 'object' || d === null) return false;
  const x = d as Record<string, unknown>;
  const usps = x.usps as Record<string, unknown> | null | undefined;
  return (
    typeof x.originaltext === 'string' &&
    ['keywords', 'ueberschriften', 'entities', 'inhalte', 'wettbewerber', 'faq'].every((k) =>
      Array.isArray(x[k]),
    ) &&
    typeof usps === 'object' &&
    usps !== null &&
    ['offen', 'platziert', 'schwach'].every((k) => Array.isArray(usps[k]))
  );
}

/** Detaildaten des PDP-Befunds ueber die Zwischenschicht (Architekturregel 1). */
export async function holeBefundDetails(runId: string): Promise<PdpDetails> {
  const daten = await ruf<PdpDetails>(
    `/api/runs/${encodeURIComponent(runId)}/befund-details`,
    'GET',
  );
  // Wie bei `holeVorschau`: die Form HIER pruefen, sonst haengt die Seite im Ladezustand.
  if (!istPdpDetails(daten)) {
    throw new ApiError('antwort_unlesbar', ALLGEMEINER_FEHLERTEXT, 200);
  }
  return daten;
}

/** Detaildaten EINER Seite einer Ratgeber-Analyse (Spec 2026-10-13-ratgeber-analyse-frontend, 6.2). */
export async function holeAnalyseSeitenDetails(
  runId: string,
  auditId: string,
): Promise<PdpDetails> {
  const daten = await ruf<PdpDetails>(
    `/api/runs/${encodeURIComponent(runId)}/seiten/${encodeURIComponent(auditId)}/details`,
    'GET',
  );
  if (!istPdpDetails(daten)) {
    throw new ApiError('antwort_unlesbar', ALLGEMEINER_FEHLERTEXT, 200);
  }
  return daten;
}

/** Entscheidung je Seite einer Ratgeber-Analyse (`POST /runs/:id/seiten/:auditId/entscheidung`). */
export async function sendeAnalyseSeitenEntscheidung(
  runId: string,
  auditId: string,
  aktion: PdpAktion,
  angaben?: PdpEntscheidungsAngaben,
): Promise<void> {
  await ruf<{ angenommen: true }>(
    `/api/runs/${encodeURIComponent(runId)}/seiten/${encodeURIComponent(auditId)}/entscheidung`,
    'POST',
    angaben ? { aktion, ...angaben } : { aktion },
  );
}

/**
 * "Dennoch volle Analyse durchfuehren" (`POST /runs/:id/volle-analyse`, Spec 2026-11-03-kein-bedarf,
 * 4.2): legt einen Kind-Auftrag mit erzwungener Analyse an oder liefert den vorhandenen. `url` nur,
 * wenn gesetzt (Ratgeber-Eintrag); beim PDP-Auftrag nimmt das Backend die Befund-URL.
 *
 * Body-Vertrag mit dem Backend: mit `url` geht `{ url }` mit, ohne `url` ein leeres Objekt. Fehlt
 * `url` bei einem Analyse-Ursprung, antwortet das Backend mit `400 url_fehlt`.
 */
export async function starteVolleAnalyse(
  runId: string,
  url?: string,
): Promise<{ kindRunId: string }> {
  const daten = await ruf<{ kindRunId?: unknown }>(
    `/api/runs/${encodeURIComponent(runId)}/volle-analyse`,
    'POST',
    url === undefined ? {} : { url },
  );
  // Ohne Kennung gaebe es nichts, wohin der Link fuehren koennte.
  if (typeof daten.kindRunId !== 'string' || daten.kindRunId === '') {
    throw new ApiError('antwort_unlesbar', ALLGEMEINER_FEHLERTEXT, 200);
  }
  return { kindRunId: daten.kindRunId };
}

/**
 * Speichert den Zwischenstand der Bearbeitung. `runde` gehoert zwingend dazu (Ruling 25):
 * Der Server weist damit einen Autosave ab, der erst nach der Entscheidung dieser Runde
 * eintrifft -- etwa aus einem zweiten, noch offenen Tab. Ohne die Runde kann er ihn nicht
 * von der ersten Eingabe der naechsten Runde unterscheiden.
 *
 * `segmentierung` (Ruling 34, optional): die Segment-Zuordnung des Entwurfs zum Zeitpunkt
 * dieses Speicherns -- ersetzt beim naechsten Laden die heuristische Ankersuche im
 * Frontend. Optional, weil die Aufruferin sie nicht hat, wenn `struktur` fuer diesen Lauf
 * fehlt (siehe `freigabe-page.tsx`); fehlt sie im Koerper ganz (statt `undefined`
 * mitzuschicken), bleibt das Verhalten fuer diesen Fall unveraendert.
 *
 * `anmerkungen` reist immer als Liste mit, auch leer (AGENTS.md Regel 5): Nur so ist im
 * gespeicherten Entwurf "keine Anmerkung mehr" von einem aelteren Entwurf ohne das Feld zu
 * unterscheiden.
 */
export async function speichereEntwurf(
  runId: string,
  text: string,
  runde: number,
  segmentierung: EntwurfSegmentEintrag[] | undefined,
  anmerkungen: Anmerkung[],
): Promise<void> {
  await ruf<{ gespeichert: true }>(`/api/runs/${encodeURIComponent(runId)}/entwurf`, 'PUT', {
    text,
    runde,
    ...(segmentierung ? { segmentierung } : {}),
    anmerkungen,
  });
}

/**
 * Verwirft einen gespeicherten Zwischenstand explizit (Knopf "Fassung aus n8n laden",
 * Task-13-Review HOCH-2) -- ohne diesen Aufruf ueberlebte der Entwurf in Firestore und
 * gewaenne beim naechsten Oeffnen erneut gegen die Fassung aus n8n. `runde` gehoert
 * zwingend dazu (Codex-Gate-Befund HOCH, Fix-Runde 6, Spiegelbild von Ruling 25): ohne sie
 * koennte ein verzoegertes Loeschen den Entwurf einer inzwischen anderen, aktuellen Runde
 * entfernen. Als Query-Parameter, nicht im Koerper -- `DELETE` bleibt bewusst koerperlos
 * (siehe `ruf` oben), dieselbe Behandlung wie `GET`.
 */
export async function loescheEntwurf(runId: string, runde: number): Promise<void> {
  await ruf<{ geloescht: true }>(
    `/api/runs/${encodeURIComponent(runId)}/entwurf?runde=${runde}`,
    'DELETE',
  );
}
